import { ResponseTemplatePath } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ResponseTemplateCreator/ResponseTemplatePath';

import { escapeModelId } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ResponseTemplateCreator/ResponseTemplateCreatorStep';
import { TextCollectionResponse } from '@scale/shared/types/response/textcollection';
import {
  InteractionStepType,
  InteractionStepConfig,
  StepParamsForType,
  StepParamsForTypeWithStage,
} from '@scale/shared/types/chat';
import { ReviewLevel } from '@scale/shared-types';
import _ from 'lodash';
import {
  isEmptyResponse,
  createDefaultResponseForFields,
  findPathsToAnnotationsForTemplating,
  getValidValuesForFieldId,
  validateRequiredResponses,
} from '@scale/shared/textcollection/helpers';
import { IMultiTurnThreadHistory } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ThreadHistory.interface';
import {
  IndexedInteractionStepResponse,
  TextCollectionPerResponseStepResponse,
} from '@scale/shared/types/chat/response';
import TextCollectionPerResponseConfigurationComponent from './Components/TextCollectionPerResponseConfigurationComponent';
import { areTextCollectionResponsesConsistent } from 'react-scale/components/scalers/tasks/ThreadedInteraction/utils';
import { isConditionallyVisible, isStepVisible } from '@scale/shared/chat/api';

import { schema } from './validation';
import StepDescriptor from '../stepDescriptor/StepDescriptor';
import TextCollectionPerResponseViewComponent from './Components/TextCollectionPerResponseViewComponent';
import TextCollectionPerResponsePinnedViewComponent from './Components/TextCollectionPerResponsePinnedViewComponent';
import { registerStepDescriptor } from '../stepDescriptor/stepDescriptorContainer';

export class TextCollectionPerResponseDescriptor extends StepDescriptor<InteractionStepType.TextCollectionPerResponse> {
  type = InteractionStepType.TextCollectionPerResponse;
  title = InteractionStepConfig[InteractionStepType.TextCollectionPerResponse].title;
  description = InteractionStepConfig[InteractionStepType.TextCollectionPerResponse].description;

  validationSchema = schema;

  ViewComponent = TextCollectionPerResponseViewComponent;
  PinnedViewComponent = TextCollectionPerResponsePinnedViewComponent;

  ConfigurationComponent = TextCollectionPerResponseConfigurationComponent;

  isResponseValid(
    response: IndexedInteractionStepResponse,
    stepParams: StepParamsForTypeWithStage<InteractionStepType.TextCollectionPerResponse>,
    history: IMultiTurnThreadHistory,
    reviewLevel?: ReviewLevel,
  ) {
    if (
      !isStepVisible(stepParams, reviewLevel) ||
      !isConditionallyVisible(
        stepParams,
        history.responses,
        response.index,
        history.threadParams.templateVariables,
        reviewLevel,
      )
    ) {
      return { canSubmit: true, submitTooltip: '' };
    }

    const { fieldToCheck, fields } = stepParams.params;
    const incompleteStepTooltip = 'Some per-response steps have not been completed.';

    // Check if the response is empty
    if (_.isEmpty(response.context.annotations)) {
      return {
        canSubmit: false,
        submitTooltip: incompleteStepTooltip,
      };
    }

    // Validate the response for each candidate
    for (const responseForCandidate of Object.values(
      (response as TextCollectionPerResponseStepResponse).context.annotations,
    )) {
      if (isEmptyResponse(responseForCandidate)) {
        return {
          canSubmit: false,
          submitTooltip: incompleteStepTooltip,
        };
      }
      const { isValid } = validateRequiredResponses(
        fields,
        responseForCandidate.annotations,
        history.threadParams.templateVariables,
      );

      if (!isValid) {
        return {
          canSubmit: false,
          submitTooltip: incompleteStepTooltip,
        };
      }
    }

    // Validate the rating consistency
    const modelResponses = history.getMostRecentResponseForStepType(
      InteractionStepType.ModelResponseSelector,
    )!;

    const selectedId = modelResponses?.context.selectedId;
    if (fieldToCheck && selectedId) {
      const { isValid, errorMessage } = areTextCollectionResponsesConsistent(
        response.context.annotations,
        fields,
        fieldToCheck,
        selectedId,
      );

      if (!isValid) {
        return { canSubmit: false, submitTooltip: errorMessage };
      }
    }

    return { canSubmit: true, submitTooltip: '' };
  }

  getResponseTemplatePaths(
    stepParams: StepParamsForType<InteractionStepType.TextCollectionPerResponse>,
    history: IMultiTurnThreadHistory,
  ) {
    const emptyResponse = createDefaultResponseForFields(stepParams.params.fields);
    const paths: string[] = findPathsToAnnotationsForTemplating(emptyResponse);
    const modelResponses = history.getMostRecentResponseForStepType(
      InteractionStepType.ModelResponseSelector,
    )!;
    const allPaths: ResponseTemplatePath[] = [];
    for (const [candidateIndex, candidate] of modelResponses?.context?.candidates?.entries() ??
      []) {
      for (const path of paths) {
        const fieldId = _.last(path.split('.'));
        if (!fieldId) {
          // Should not happen
          allPaths.push({ path: `context.response.${path}.response[0]`, candidateIndex, fieldId });
          continue;
        }
        const {
          values: expectedValues,
          valuesWithLabels: expectedValuesWithLabels,
          expectsArray,
          fieldTitle,
        } = getValidValuesForFieldId(stepParams.params.fields, fieldId) ?? {};
        allPaths.push({
          path: `context.annotations.${escapeModelId(candidate.id)}.${path}.response[0]`,
          label: `${fieldTitle} (${candidate.id}.${fieldId})`,
          expectsArray,
          expectedValues,
          expectedValuesWithLabels,
          parseAsPrimitive: expectedValues
            ? _.some(expectedValues, (v: any) => !_.isString(v))
            : undefined,
          fieldId,
          candidateIndex,
        });
      }
    }
    return allPaths;
  }

  generateEmptyResponse(
    stepParams: StepParamsForType<InteractionStepType.TextCollectionPerResponse>,
    history: IMultiTurnThreadHistory,
  ) {
    const modelResponses = history.getMostRecentResponseForStepType(
      InteractionStepType.ModelResponseSelector,
    )!;
    const emptyResponse: Record<string, TextCollectionResponse> = {};
    for (const candidate of modelResponses?.context?.candidates ?? []) {
      emptyResponse[candidate.id] = createDefaultResponseForFields(stepParams.params.fields);
    }
    return {
      type: InteractionStepType.TextCollectionPerResponse,
      context: {
        candidates: modelResponses.context.candidates,
        annotations: emptyResponse,
        selectedId: modelResponses.context.selectedId,
      },
      output: history.getMostRecentResponse(),
    };
  }
}

registerStepDescriptor({
  type: InteractionStepType.TextCollectionPerResponse,
  descriptor: new TextCollectionPerResponseDescriptor(),
});
